import { Color, Group, PerspectiveCamera, Scene, Clock, WebGLRenderer } from 'three';

import type { QualitySettings } from '../../../hooks/use-three-kitchen/quality';
import type { QualityLevel } from '../../types/engine.types';
import type { SceneManagerConfig } from './types/config.types';
import { RenderScheduler } from './render-scheduler.class';
import { resolveBackgroundColor } from './helpers/resolve-background-color.helper';
import { initializeCfn } from './functions/initialize.cfn';
import { setQualityCfn } from './functions/set-quality.cfn';
import { startRenderLoopCfn } from './functions/start-render-loop.cfn';
import { disposeCfn } from './functions/dispose.cfn';

/**
 * SceneManager handles the core Three.js rendering infrastructure:
 * - WebGLRenderer with quality-based settings
 * - Scene with background configuration
 * - PerspectiveCamera
 * - Clock for animation timing
 * - Resize handling
 *
 * This is a domain-agnostic manager that knows nothing about kitchens,
 * blueprints, or specific scene content.
 *
 * @example
 * ```typescript
 * const sceneManager = new SceneManager({
 *   container: document.getElementById('canvas'),
 *   backgroundColor: '#ffffff',
 *   quality: 7
 * });
 *
 * sceneManager.initialize();
 *
 * // In render loop
 * sceneManager.render();
 *
 * // Cleanup
 * sceneManager.dispose();
 * ```
 */
export class SceneManager {
	#config: SceneManagerConfig;
	#renderer: WebGLRenderer | null = null;
	#scene: Scene | null = null;
	#camera: PerspectiveCamera | null = null;
	#clock: Clock | null = null;
	#qualitySettings: QualitySettings | null = null;
	#resizeHandler: (() => void) | null = null;
	#resizeObserver: ResizeObserver | null = null;
	#resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
	#resizeSettleTimer: ReturnType<typeof setTimeout> | null = null;
	#animationFrameId: number | null = null;
	#renderScheduler: RenderScheduler | null = null;
	#isActive = false;
	#backgroundColor: Color;
	#objectHolder: Group | null = null;

	constructor(config: SceneManagerConfig) {
		this.#config = config;
		this.#backgroundColor = resolveBackgroundColor(config.backgroundColor, config.container);
	}

	// ─────────────────────────────────────────────────────────────────────────────
	// Public getters for external read access
	// ─────────────────────────────────────────────────────────────────────────────

	/**
	 * Get the WebGL renderer
	 */
	get renderer(): WebGLRenderer | null {
		return this.#renderer;
	}

	/**
	 * Get the scene
	 */
	get scene(): Scene | null {
		return this.#scene;
	}

	/**
	 * Get the camera
	 */
	get camera(): PerspectiveCamera | null {
		return this.#camera;
	}

	/**
	 * Get the clock
	 */
	get clock(): Clock | null {
		return this.#clock;
	}

	/**
	 * Get the quality settings
	 */
	get qualitySettings(): QualitySettings | null {
		return this.#qualitySettings;
	}

	/**
	 * Get whether the scene is active
	 */
	get isActive(): boolean {
		return this.#isActive;
	}

	/**
	 * Get the object holder group (for adding scene objects)
	 */
	get objectHolder(): Group | null {
		return this.#objectHolder;
	}

	/**
	 * Get the container element
	 */
	get container(): HTMLElement {
		return this.#config.container;
	}

	/**
	 * Get the animation frame ID
	 */
	get animationFrameId(): number | null {
		return this.#animationFrameId;
	}

	/**
	 * Get the render scheduler
	 */
	get renderScheduler(): RenderScheduler | null {
		return this.#renderScheduler;
	}

	/**
	 * Get the configuration
	 */
	get config(): SceneManagerConfig {
		return this.#config;
	}

	/**
	 * Get the background color
	 */
	get backgroundColor(): Color {
		return this.#backgroundColor;
	}

	/**
	 * Get the resize handler
	 */
	get resizeHandler(): (() => void) | null {
		return this.#resizeHandler;
	}

	/**
	 * Get the container ResizeObserver
	 */
	get resizeObserver(): ResizeObserver | null {
		return this.#resizeObserver;
	}

	/**
	 * Get the pending resize debounce timer (for cfn function access)
	 */
	get resizeDebounceTimer(): ReturnType<typeof setTimeout> | null {
		return this.#resizeDebounceTimer;
	}

	/**
	 * Get the pending resize settle timer (for cfn function access)
	 */
	get resizeSettleTimer(): ReturnType<typeof setTimeout> | null {
		return this.#resizeSettleTimer;
	}

	// ─────────────────────────────────────────────────────────────────────────────
	// Setters for cfn function access
	// ─────────────────────────────────────────────────────────────────────────────

	/**
	 * Set the renderer (for cfn functions)
	 */
	setRenderer(renderer: WebGLRenderer | null): void {
		this.#renderer = renderer;
	}

	/**
	 * Set the scene (for cfn functions)
	 */
	setScene(scene: Scene | null): void {
		this.#scene = scene;
	}

	/**
	 * Set the camera (for cfn functions)
	 */
	setCamera(camera: PerspectiveCamera | null): void {
		this.#camera = camera;
	}

	/**
	 * Set the clock (for cfn functions)
	 */
	setClock(clock: Clock | null): void {
		this.#clock = clock;
	}

	/**
	 * Set the quality settings (for cfn functions)
	 */
	setQualitySettings(settings: QualitySettings | null): void {
		this.#qualitySettings = settings;
	}

	/**
	 * Set the resize handler (for cfn functions)
	 */
	setResizeHandler(handler: (() => void) | null): void {
		this.#resizeHandler = handler;
	}

	/**
	 * Set the container ResizeObserver (for cfn functions)
	 */
	setResizeObserver(observer: ResizeObserver | null): void {
		this.#resizeObserver = observer;
	}

	/**
	 * Set the pending resize debounce timer (for cfn functions)
	 */
	setResizeDebounceTimer(timer: ReturnType<typeof setTimeout> | null): void {
		this.#resizeDebounceTimer = timer;
	}

	/**
	 * Set the pending resize settle timer (for cfn functions)
	 */
	setResizeSettleTimer(timer: ReturnType<typeof setTimeout> | null): void {
		this.#resizeSettleTimer = timer;
	}

	/**
	 * Set the animation frame ID (for cfn functions)
	 */
	setAnimationFrameId(id: number | null): void {
		this.#animationFrameId = id;
	}

	/**
	 * Set the render scheduler (for cfn functions)
	 */
	setRenderScheduler(scheduler: RenderScheduler | null): void {
		this.#renderScheduler = scheduler;
	}

	/**
	 * Set whether the scene is active (for cfn functions)
	 */
	setIsActive(active: boolean): void {
		this.#isActive = active;
	}

	/**
	 * Set the object holder (for cfn functions)
	 */
	setObjectHolder(holder: Group | null): void {
		this.#objectHolder = holder;
	}

	// ─────────────────────────────────────────────────────────────────────────────
	// Public methods
	// ─────────────────────────────────────────────────────────────────────────────

	/**
	 * Initialize the scene manager
	 * Creates renderer, scene, camera, clock, and sets up resize handling
	 */
	initialize(): void {
		initializeCfn(this, {});
	}

	/**
	 * Set the background color
	 */
	setBackgroundColor(color: string | Color): void {
		this.#backgroundColor = color instanceof Color ? color : new Color(color);
		if (this.#scene) {
			this.#scene.background = this.#backgroundColor;
		}
	}

	/**
	 * Update quality settings
	 */
	setQuality(quality: QualityLevel): void {
		setQualityCfn(this, { quality });
	}

	/**
	 * Render a single frame
	 */
	render(): void {
		if (this.#renderer && this.#scene && this.#camera) {
			this.#renderer.render(this.#scene, this.#camera);
		}
	}

	/**
	 * Get delta time from clock
	 */
	getDelta(): number {
		return this.#clock?.getDelta() ?? 0;
	}

	/**
	 * Get elapsed time from clock
	 */
	getElapsedTime(): number {
		return this.#clock?.getElapsedTime() ?? 0;
	}

	/**
	 * Start the render loop.
	 * Creates a RenderScheduler (if not already created) and starts
	 * its continuous loop.
	 *
	 * @param onFrame Optional callback for each frame (receives delta time)
	 * @param shouldContinue Optional callback that returns true while the loop
	 *                       should keep running.  Defaults to always-true so the
	 *                       loop runs until explicitly stopped.
	 */
	startRenderLoop(onFrame?: (delta: number) => void, shouldContinue?: () => boolean): void {
		startRenderLoopCfn(this, { onFrame, shouldContinue });
	}

	/**
	 * Stop the render loop.
	 * Stops the RenderScheduler's continuous loop (if active) and cancels
	 * any legacy rAF handle.
	 */
	stopRenderLoop(): void {
		// Stop the scheduler's loop
		if (this.#renderScheduler) {
			this.#renderScheduler.stopLoop();
		}

		// Legacy fallback: cancel any raw rAF handle
		if (this.#animationFrameId !== null) {
			cancelAnimationFrame(this.#animationFrameId);
			this.#animationFrameId = null;
		}
	}

	/**
	 * Request a single re-render on the next animation frame.
	 * Multiple calls within the same frame are coalesced into one render.
	 * If the continuous loop is running, this is a no-op.
	 */
	requestRender(): void {
		this.#renderScheduler?.requestRender();
	}

	/**
	 * Force a resize update
	 */
	resize(): void {
		if (this.#resizeHandler) {
			this.#resizeHandler();
		}
	}

	/**
	 * Dispose of all resources
	 */
	dispose(): void {
		disposeCfn(this, {});
	}
}
